import React, { Component } from 'react'
import { VariableSizeGrid as Grid } from 'react-window'
import isEqual from 'lodash/isEqual'
import { HeaderCell } from './components'
import get from 'lodash/get'
import Link from 'next/link'
import { Icon } from 'src/components/Icon'
import bem from './bem'
import { ENTITY } from 'src/consts'
import styles from './styles.module.scss'
import { exchangeMaketsReferalMap } from 'src/pages/ExchangeDetails/config'

const cName = bem.with('EntiteMarkets')

const CELL_STYLES = {
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid hsla(0,0%,100%,0.15)',
  padding: '0 10px',
  height: '50px',
  backgroundColor: '#1d1f1e',
  boxSizing: 'border-box',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const Wrapper = ({ isLink = false, children, page, ...props }) => {
  if (isLink) {
    return (
      <Link href={page} {...props} legacyBehavior>
        <a
          onClick={() =>
            sessionStorage.setItem(
              'lastDataPage',
              `${location.pathname}${location.search}`
            )
          }
        >
          {children}
        </a>
      </Link>
    )
  } else {
    return <div {...props}>{children}</div>
  }
}

export class MarketsComponent extends Component {
  componentDidUpdate(prevProps) {
    const { isMobile, data, width, tableConfig } = this.props

    if (
      (prevProps.isMobile !== isMobile && this.outerRef) ||
      !isEqual(prevProps.data, data) ||
      prevProps.width !== width
    ) {
      const tableConfigLength = Object.keys(tableConfig).length

      Array.from(Array(tableConfigLength).keys()).forEach(i => {
        this.outerRef.current.resetAfterColumnIndex(i)
      })
    }
  }

  outerRef = React.createRef()

  getColumnWidth = index => {
    const { isMobile, width, data, tableConfig } = this.props

    const symbolWidth = 6.75
    const columnKey = tableConfig[index].key
    const columnData = data.map(i => i[columnKey])

    const getColumnMaxWidth = data =>
      Math.max(...data.map(i => String(i).length)) * symbolWidth + 35

    const columnMaxWidth = getColumnMaxWidth(columnData)

    const containerMaxWidth = isMobile ? 900 : 1140
    let columnRelWidth = (width / 100) * tableConfig[index].relWidth - 5
    if (width <= containerMaxWidth) {
      columnRelWidth = (containerMaxWidth / 100) * tableConfig[index].relWidth
    }

    return columnMaxWidth < columnRelWidth || columnKey === 'feeType'
      ? columnRelWidth
      : columnMaxWidth
  }

  getRowData = rowIndex =>
    this.props.data.find((market, index) => index === rowIndex - 1)

  getRowPage = rowData => {
    const { entity } = this.props
    return {
      [ENTITY.COIN]: `/${ENTITY.EXCHANGE}/${get(rowData, 'exchange')}`,
      [ENTITY.EXCHANGE]:
        get(rowData, 'coin.slug') && get(rowData, 'coin.symbol')
          ? `/${ENTITY.COIN}/${get(rowData, 'coin.slug')}`
          : '#',
    }[entity]
  }

  renderFixedHeaderCell = ({ key, columnIndex, style }) => {
    const {
      handleSortingChange,
      sortBy,
      sortDirection,
      isMobile,
      tableConfig,
    } = this.props
    const {
      isSortable,
      justifyContent,
      key: columnKey,
      headerName,
      mobileHeaderName,
    } = tableConfig[columnIndex]
    const isSortingActive =
      columnKey === 'updated' ? sortBy === 'timestamp' : sortBy === columnKey

    return (
      <HeaderCell
        key={key}
        style={{
          ...style,
          flexDirection: justifyContent === 'flex-end' ? 'row' : 'row-reverse',
        }}
        sortDirection={sortDirection}
        isMobile={isMobile}
        isSortable={isSortable}
        className={cName('HeaderCell', {
          active: isSortingActive,
          'not-sortable': !isSortable,
        })}
        onClick={
          isSortable
            ? handleSortingChange(
                columnKey === 'updated' ? 'timestamp' : columnKey
              )
            : _ => _
        }
      >
        {isMobile ? mobileHeaderName : headerName}
      </HeaderCell>
    )
  }

  renderFixedColumnCell = ({ key, rowIndex, style }) => {
    const { entity } = this.props
    const rowData = this.getRowData(rowIndex)
    const isExchange = entity === ENTITY.EXCHANGE
    const iconData = {
      slug: get(rowData, 'coin.slug') || rowData.exchange,
      name: isExchange ? get(rowData, 'coin.name') || '' : '',
      symbol: isExchange ? get(rowData, 'coin.symbol') || '' : '',
    }
    const text = isExchange
      ? get(rowData, 'coin.name') || get(rowData, 'name')
      : rowData.name

    const page = this.getRowPage(rowData)
    const isLinkParam = get(rowData, 'coin.symbol', text)
    const styleOverride = {}
    const isFiat = rowData.coin?.category === 'fiat'

    return (
      <div
        key={key}
        style={{ ...style, ...styleOverride, justifyContent: 'flex-start' }}
        data-index={rowData.index}
      >
        <div
          style={{ marginLeft: 5, width: 40, minWidth: 40 }}
          data-page={page}
        >
          {rowData.index}
        </div>
        <Wrapper
          isLink={isLinkParam && !isFiat}
          style={{ marginLeft: 10, overflow: 'hidden' }}
          page={page}
        >
          {
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              data-page={page}
            >
              {
                <Icon
                  data={iconData}
                  size='16x16'
                  timeout
                  entity={isExchange ? ENTITY.COIN : ENTITY.EXCHANGE}
                />
              }
              {text && (
                <div
                  style={{
                    marginLeft: 8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  data-page={page}
                  className='MarketsText'
                >
                  {text.charAt(0).toUpperCase()}
                  {text.slice(1)}
                </div>
              )}
            </div>
          }
        </Wrapper>
      </div>
    )
  }

  goToExchange = () => {
    const { exchangeLink } = this.props

    if (exchangeLink) {
      window.open(exchangeLink, '_blank', 'noopener,noreferrer')
    }
  }

  renderDataCell = ({ key, rowIndex, columnIndex, style }) => {
    const { tableConfig, entity, exchangeLink, mapDataArray, slug } = this.props
    const {
      justifyContent,
      key: columnKey,
      defaultValue,
    } = tableConfig[columnIndex]
    const rowData = this.getRowData(rowIndex)
    const value = rowData[columnKey]
    const styleOverride = {}
    const referal = exchangeMaketsReferalMap[slug]

    let cellContent = value || defaultValue || '—'

    if (
      entity === ENTITY.EXCHANGE &&
      exchangeLink &&
      columnKey === 'exchangePair'
    ) {
      cellContent = (
        <a
          href={exchangeLink}
          target='_blank'
          rel='noopener noreferrer nofollow'
          key={key}
          data-index={rowData.index}
        >
          {cellContent}
        </a>
      )
    }

    if (columnKey === 'market') {
      const coin = mapDataArray.find(
        i => i.slug === rowData['quote'] && i.category !== 'fiat'
      )

      if (coin) {
        const url =
          rowData['quote'] === slug
            ? `/coin/${rowData['base']}`
            : `/coin/${rowData['quote']}`
        cellContent = (
          <a
            className={styles.marketColumn}
            href={url}
            rel='noopener noreferrer nofollow'
            key={key}
            data-index={rowData.index}
          >
            {cellContent}
          </a>
        )
      }
    }

    if (columnKey === 'exchangePair') {
      if (referal) {
        const url = referal.replace('{symbol}', rowData.symbol)
        cellContent = (
          <a
            className={styles.marketColumn}
            href={url}
            rel='noopener noreferrer nofollow'
            key={key}
            target={'_blank'}
            data-index={rowData.index}
          >
            {cellContent}
          </a>
        )
      }
    }

    return (
      <div
        key={key}
        style={{ ...style, ...CELL_STYLES, ...styleOverride, justifyContent }}
        className={`Markets__Grid__Cell ${columnKey} ${
          columnKey !== 'exchangePair'
            ? 'Markets__Grid__Cell--disableHover'
            : ''
        }`}
        data-index={rowData.index}
      >
        {cellContent}
      </div>
    )
  }

  renderTopLeftHeaderCell = () => {
    const { isMobile, sortBy, sortDirection, handleSortingChange, entity } =
      this.props

    return (
      <div
        style={{
          ...CELL_STYLES,
          zIndex: 15,
          display: 'flex',
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontSize: this.props.isMobile ? '12px' : '16px',
          borderTop: '1px solid hsla(0, 0%, 100%, 0.15)',
          boxShadow: isMobile ? '4px 0 3px rgba(0, 0, 0, 0.1)' : null,
        }}
      >
        <HeaderCell
          style={{
            height: 49,
            marginLeft: 5,
            width: 40,
            display: 'flex',
            alignItems: 'center',
            borderTop: 'none',
            cursor: 'pointer',
            justifyContent: 'flex-end',
            flexDirection: 'row-reverse',
          }}
          sortDirection={sortDirection}
          isMobile={isMobile}
          isSortable
          className={cName('HeaderCell', { active: sortBy === 'index' })}
          onClick={handleSortingChange('index')}
        >
          #
        </HeaderCell>
        <HeaderCell
          style={{
            height: 49,
            paddingLeft: 10,
            width: this.getColumnWidth(0) - 65,
            display: 'flex',
            alignItems: 'center',
            borderTop: 'none',
            justifyContent: 'flex-end',
            flexDirection: 'row-reverse',
          }}
          sortDirection={sortDirection}
          isMobile={isMobile}
          isSortable
          className={cName('HeaderCell', {
            active: sortBy === 'base' || sortBy === 'exchange',
          })}
          onClick={handleSortingChange(
            entity === ENTITY.EXCHANGE ? 'base' : 'exchange'
          )}
        >
          {entity === ENTITY.EXCHANGE ? 'Currency' : 'Source'}
        </HeaderCell>
      </div>
    )
  }

  renderCell = ({ columnIndex, rowIndex, style }) => {
    const key = `${rowIndex}-${columnIndex}`
    const cellStyles = {
      ...style,
      ...CELL_STYLES,
      fontSize: this.props.isMobile ? '12px' : '16px',
    }

    const isFixedHeaderCell = rowIndex === 0
    if (isFixedHeaderCell)
      return this.renderFixedHeaderCell({ key, columnIndex, style: cellStyles })

    const isFixedColumnCell = columnIndex === 0
    if (isFixedColumnCell)
      return this.renderFixedColumnCell({ key, rowIndex, style: cellStyles })

    return this.renderDataCell({
      key,
      rowIndex,
      columnIndex,
      style: cellStyles,
    })
  }

  render() {
    const { data, width, height, onScroll, tableConfig } = this.props

    if (data.length === 0) return null
    return (
      <>
        {this.renderTopLeftHeaderCell()}
        <Grid
          onScroll={onScroll}
          ref={this.outerRef}
          className='Markets__Grid Markets__Grid--with-box-shadow'
          width={width}
          height={height}
          columnCount={Object.keys(tableConfig).length}
          columnWidth={this.getColumnWidth}
          rowCount={data.length + 1}
          rowHeight={() => 50}
          overscanRowsCount={10}
          overscanColumnsCount={5}
        >
          {this.renderCell}
        </Grid>
      </>
    )
  }
}
